import React from 'react';
import { Box, Typography, Button, Container, Grid, useMediaQuery } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import AnimatedElement from './AnimatedElement';
import TelegramIcon from '@mui/icons-material/Telegram';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

// Styled components
const HeroContainer = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 50%, ${theme.palette.telegram.main} 100%)`,
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
  overflow: 'hidden',
  color: '#fff',
  paddingTop: theme.spacing(10),
  paddingBottom: theme.spacing(10),
}));

const CircleDecoration = styled(Box)(({ theme, size, top, left, right, opacity }) => ({
  position: 'absolute',
  width: size,
  height: size,
  borderRadius: '50%',
  background: 'rgba(255, 255, 255, 0.1)',
  top: top,
  left: left,
  right: right,
  opacity: opacity,
  zIndex: 0,
}));

const HeroImageContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  [theme.breakpoints.down('md')]: {
    marginTop: theme.spacing(5),
  },
}));

const HeroImage = styled('img')(({ theme }) => ({
  maxWidth: '60%',
  width: '500px',
  height: 'auto',
  borderRadius: 24,
  boxShadow: '0 30px 60px rgba(0, 0, 0, 0.25)',
  transform: 'perspective(1000px) rotateY(-5deg)',
  transition: 'transform 0.3s ease',
  '&:hover': {
    transform: 'perspective(1000px) rotateY(0deg)',
  },
}));

const TelegramWindow = styled(Box)(({ theme }) => ({
  backgroundColor: '#fff',
  borderRadius: 24,
  padding: theme.spacing(3),
  boxShadow: '0 30px 60px rgba(0, 0, 0, 0.25)',
  maxWidth: '100%',
  width: '450px',
  position: 'relative',
  zIndex: 2,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '60px',
    backgroundColor: theme.palette.telegram.main,
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    zIndex: -1,
  },
}));

const TelegramHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
  position: 'relative',
  marginTop: '20px',
}));

const TelegramContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  marginBottom: theme.spacing(1),
}));

const TelegramSlide = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
  borderRadius: 12,
  width: '100%',
  height: '150px',
  marginBottom: theme.spacing(2),
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  fontSize: '18px',
  fontWeight: 500,
  color: theme.palette.text.primary,
}));

const TelegramButtons = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  marginTop: theme.spacing(2),
}));

const TelegramButton = styled(Button)(({ theme, color = 'primary' }) => ({
  backgroundColor: theme.palette[color].main,
  color: '#fff',
  borderRadius: 8,
  fontWeight: 500,
  '&:hover': {
    backgroundColor: theme.palette[color].dark,
  },
}));

const HeroSection = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <HeroContainer>
      {/* Decorative circles */}
      <CircleDecoration size="450px" top="-150px" left="-100px" opacity={0.1} />
      <CircleDecoration size="300px" top="60%" right="-50px" opacity={0.08} />
      <CircleDecoration size="200px" top="20%" left="65%" opacity={0.05} />

      <Container maxWidth="lg">
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={6}>
            <AnimatedElement animation="fadeInUp" delay={0.2}>
              <Typography 
                variant="h1" 
                component="h1" 
                gutterBottom
                sx={{ 
                  fontWeight: 700, 
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  textShadow: '0 2px 10px rgba(0,0,0,0.2)'
                }}
              >
                Вебинары прямо в Telegram
              </Typography>
            </AnimatedElement>

            <AnimatedElement animation="fadeInUp" delay={0.4}>
              <Typography 
                variant="h5" 
                component="p" 
                sx={{ mb: 4, opacity: 0.9, maxWidth: '600px' }}
              >
                Проводите продающие вебинары эффективнее! Увеличьте конверсию на 20% благодаря интеграции с Telegram и верифицированным контактам участников.
              </Typography>
            </AnimatedElement>

            <AnimatedElement animation="fadeInUp" delay={0.6}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                <Button 
                  variant="contained" 
                  size="large" 
                  color="secondary"
                  startIcon={<TelegramIcon />}
                  href="https://t.me/webigramhost_bot"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ 
                    px: 4, 
                    py: 1.5,
                    fontSize: '1.1rem',
                    boxShadow: '0 8px 20px rgba(255, 64, 129, 0.3)'
                  }}
                >
                  Начать бесплатно
                </Button>
              </Box>
            </AnimatedElement>
          </Grid>

          <Grid item xs={12} md={6}>
            <HeroImageContainer>
              <AnimatedElement animation="fadeIn" delay={0.8}>
                <HeroImage
                  src="/images/hero-bg.png"
                  alt="Webigram Interface"
                  loading="eager"
                />
              </AnimatedElement>
            </HeroImageContainer>
          </Grid>
        </Grid>
      </Container>
    </HeroContainer>
  );
};

export default HeroSection; 